import { ChevronDown, ExternalLink } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const externalGuideLinkClassName =
  "inline-flex items-center gap-1.5 font-medium text-primary underline-offset-4 transition-colors hover:text-primary/90 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function IncomeGuideExternalLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={externalGuideLinkClassName}>
      {children}
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
    </a>
  );
}

export interface IncomeDocumentGuideSectionProps {
  /** Shown on the collapsible trigger (e.g. “How to get…?”). */
  title: string;
  /** Numbered steps, links, etc. */
  instructions: ReactNode;
  /** Typically `StyledFileInput` below the guide. */
  fileUpload: ReactNode;
  className?: string;
  defaultOpen?: boolean;
}

/**
 * “How to obtain this document” accordion plus file upload inside one card, used in the income certificate wizard.
 */
export function IncomeDocumentGuideSection({
  title,
  instructions,
  fileUpload,
  className,
  defaultOpen = false,
}: IncomeDocumentGuideSectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card
      className={cn(
        "gap-0 overflow-hidden border-border/80 bg-card p-0 shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20",
        "transition-[box-shadow] duration-200 motion-reduce:transition-none",
        open && "ring-1 ring-primary/20 dark:ring-primary/25",
        className,
      )}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          className={cn(
            "flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm font-semibold leading-snug text-foreground transition-colors duration-150",
            "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 motion-reduce:transition-none",
          )}
        >
          <span>{title}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden">
          <div className="space-y-3 border-t border-border bg-muted/15 px-4 py-3 text-xs leading-relaxed text-muted-foreground dark:bg-secondary/25 sm:text-sm sm:leading-relaxed">
            {instructions}
          </div>
        </CollapsibleContent>
      </Collapsible>
      <div className="border-t border-border px-4 py-3">{fileUpload}</div>
    </Card>
  );
}
